import React from 'react';
import PropTypes from 'prop-types';
import './Button.scss';

const IconComponent = ({ icon }) => {
  switch (icon) {
    case 'arrow':
      return <span className="btn__icon btn__icon--arrow">→</span>;
    case 'external-link':
      return <svg className="btn__icon btn__icon--external-link" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true" alt="Clickable share icon">
              <title>External link icon</title>
              <path d="M8.556 0v1.556h2.792L3.702 9.201l1.097 1.097 7.645-7.646v2.792H14V0M12.444 12.444H1.556V1.556H7V0H1.556C1.143 0 .747.164.456.456A1.556 1.556 0 000 1.556v10.888C0 12.857.164 13.253.456 13.544c.291.292.687.456 1.1.456h10.888c.413 0 .809-.164 1.1-.456.292-.291.456-.687.456-1.1V7h-1.556v5.444z" fill="currentColor"/>
            </svg>;
    case 'download':
      return <svg xmlns="http://www.w3.org/2000/svg" width="17" height="16" viewBox="0 0 17 16" fill="none" alt="Download arrow">
              <title>Download icon</title>
              <path d="M8.34657 0.875V12.5417M2.90213 7.09722L8.34657 12.5417L13.791 7.09722" stroke="white" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M12.4988 14.875H0.832174H15.9988" stroke="white" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>;
    case 'download2':
      return <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none" alt="Download arrow">
                <title>Download icon</title>
                <g clipPath="url(#clip0_5018_20234)">
                <path d="M8.34852 2V13.6667M2.90408 8.22222L8.34852 13.6667L13.793 8.22222" stroke="#135772" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M12.4988 16H0.832174H15.9988" stroke="#135772" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
                </g>
                <defs>
                <clipPath id="clip0_5018_20234">
                <rect width="18" height="18" fill="white"/>
                </clipPath>
                </defs>
              </svg>;
      // <span className="btn__icon btn__icon--download">⬇</span>;
    default:
      return null;
  }
};

IconComponent.propTypes = {
  icon: PropTypes.oneOf(['arrow', 'external-link', 'download', 'download2']),
};

const isExternalUrl = (href) =>
  typeof href === 'string' && /^https?:\/\//.test(href);

export const Button = ({
  variant = 'primary',
  size = 'md',
  children,
  icon = 'arrow',
  disabled = false,
  href,
  onClick,
  className: extraClassName,
  ...props
}) => {
  const baseClass = [`btn btn--${variant} btn--${size}`, extraClassName]
    .filter(Boolean)
    .join(' ');

  const inner = (
    <>
      <span className="btn__content">{children}</span>
      {icon && <IconComponent icon={icon} />}
    </>
  );

  // External URL — render as anchor with js-interstitial so
  // InterstitialProvider intercepts the click automatically.
  if (href && isExternalUrl(href)) {
    return (
      <a
        className={`gtm-link-external gtm-interstitial ${baseClass} js-interstitial`}
        href={href}
        rel="noopener noreferrer"
        onClick={onClick}
        {...props}
      >
        {inner}
      </a>
    );
  }

  // Internal path — plain anchor, no interstitial.
  if (href) {
    return (
      <a className={baseClass} href={href} onClick={onClick} {...props}>
        {inner}
      </a>
    );
  }

  // Default — standard button element.
  return (
    <button className={baseClass} disabled={disabled} onClick={onClick} {...props}>
      {inner}
    </button>
  );
};

Button.propTypes = {
  variant: PropTypes.oneOf(['primary', 'secondary', 'tertiary', 'purple']),
  size: PropTypes.oneOf(['sm', 'md', 'lg', 'xl']),
  children: PropTypes.node.isRequired,
  icon: PropTypes.oneOf(['arrow', 'external-link', 'download', 'download2', null]),
  disabled: PropTypes.bool,
  /** Internal path (/page) or external URL (https://…). External URLs trigger the interstitial modal automatically. */
  href: PropTypes.string,
  onClick: PropTypes.func,
  className: PropTypes.string,
};
